import {useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {SegmentedControl} from '@/components/segmented-control'
import {useTableState} from '@/hooks/common/use-table-state'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {PlanLockedPage} from '@/components/plan-upsell'
import {ObraFormDialog} from '@/components/obra-form-dialog'
import {OBRAS_PAGE_SIZE, useObras, type Obra, type ObraEstado} from '@/hooks/empresa/obras/use-obras'
import {SearchInput} from '@/components/ui/search-input'

const FILTROS: (ObraEstado | 'todas')[] = ['todas', 'activo', 'terminado']

export function ObrasPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {businessId} = useParams<{businessId: string}>()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const table = useTableState('name')
  // En el teléfono las columnas secundarias no se montan: bajo `table-fixed` el `<colgroup>` les reserva ancho igual.
  const enTelefono = useIsMobile()
  const [estado, setEstado] = useState<ObraEstado | 'todas'>('todas')
  const [creando, setCreando] = useState(false)
  const canEdit = !useGodModeReadOnly()
  const {data, isLoading, isError, refetch} = useObras(business ? businessId : undefined, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
    extra: {estado: estado === 'todas' ? undefined : estado},
  })

  if (!businessId) return null
  if (guard) return guard
  // Toda la familia de rutas es de pago, lecturas incluidas: sin esta puerta el 402 llegaba recién al guardar.
  if (capabilities.plan_tier === 'free') {
    return <PlanLockedPage feature={t('plans.feature.obras')} businessId={businessId} businessName={business!.name} />
  }

  const fechas = (obra: Obra) =>
    obra.fecha_fin
      ? t('obra.detail.periodoCerrado', {inicio: obra.fecha_inicio, fin: obra.fecha_fin})
      : t('obra.detail.periodo', {inicio: obra.fecha_inicio})

  const columns: Column<Obra>[] = [
    {
      key: 'name',
      header: t('obra.table.name'),
      cell: (obra) => (
        <button
          type="button"
          className="text-left font-medium text-foreground hover:underline"
          onClick={() => navigate(`/businesses/${businessId}/obras/${obra.id}`)}
          data-testid={`obra-open-${obra.id}`}>
          {obra.name}
        </button>
      ),
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'fecha_inicio',
            header: t('obra.table.fechas'),
            width: 'w-44',
            cell: (obra: Obra) => <span className="font-mono text-xs text-muted-foreground">{fechas(obra)}</span>,
          } satisfies Column<Obra>,
          {
            key: 'sedes',
            header: t('obra.table.sedes'),
            width: 'w-40',
            cell: (obra: Obra) => <span className="text-xs text-muted-foreground">{obra.sedes.join(' · ')}</span>,
          } satisfies Column<Obra>,
        ]),
    {
      key: 'estado',
      header: t('obra.table.estado'),
      width: 'w-28',
      cell: (obra) => <CeldaEstado chip={<Tag tone="neutral">{t(`obra.estado.${obra.estado}`)}</Tag>} />,
    },
    {key: 'personas', header: t('obra.table.personas'), width: 'w-24', cell: (obra) => <span>{obra.personas}</span>},
    {
      key: 'peligros',
      header: t('obra.table.peligros'),
      width: 'w-32',
      // Cero peligros nunca se lee como «cumple» (D13): la celda lo nombra.
      cell: (obra) =>
        obra.peligros === 0 ? (
          <CeldaEstado chip={<Tag tone="amber">{t('obra.sinPeligrosTag')}</Tag>} />
        ) : (
          <span data-testid={`obra-peligros-${obra.id}`}>{obra.peligros}</span>
        ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="obras-page">
      <PageHeading
        title={t('obra.title')}
        accion={
          canEdit && (
            <Button size="sm" data-testid="obra-new" onClick={() => setCreando(true)}>
              {t('obra.new')}
            </Button>
          )
        }
      />

      <div className="surface overflow-hidden">
        <div className="surface-divider flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <SearchInput
            value={table.searchInput}
            onChange={table.setSearchInput}
            placeholder={t('obra.searchPlaceholder')}
          />
          <SegmentedControl
            value={estado}
            options={FILTROS.map((valor) => ({key: valor, label: t(`obra.filtro.${valor}`)}))}
            testId="obras-filtro-estado"
            onChange={(valor) => {
              setEstado(valor)
              table.setPage(1)
            }}
          />
        </div>

        {/* Sin este brazo, `data` indefinido con `isLoading` falso pintaba una tabla vacía sin encabezado. */}
        {isError ? (
          <div className="space-y-3 px-6 py-10 text-center" data-testid="obras-error-carga">
            <p className="text-sm text-foreground">{t('obra.errorCargaLista')}</p>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              {t('obra.reintentar')}
            </Button>
          </div>
        ) : isLoading && !data ? (
          <div className="p-4">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : data && data.items.length === 0 ? (
          <div className="px-6 py-14 text-center" data-testid="obras-vacio">
            <p className="font-heading text-lg text-foreground">
              {table.search ? t('obra.noResults') : t('obra.emptyTitle')}
            </p>
            {!table.search && <p className="mt-1 text-sm text-muted-foreground">{t('obra.emptyBody')}</p>}
          </div>
        ) : (
          <>
            <div className="px-1">
              <DataTable
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(obra) => obra.id}
                renderRowDetail={
                  enTelefono
                    ? (obra) => (
                        <span className="text-xs text-muted-foreground">
                          {fechas(obra)}
                          {obra.sedes.length > 0 ? ` · ${obra.sedes.join(' · ')}` : ''}
                        </span>
                      )
                    : undefined
                }
              />
            </div>
            {!!data?.total && (
              <div className="surface-divider border-t px-4 py-2.5">
                <PaginationBar
                  page={table.page}
                  pageSize={OBRAS_PAGE_SIZE}
                  total={data.total}
                  onPageChange={table.setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ObraFormDialog
        businessId={businessId}
        obra={null}
        open={creando}
        onOpenChange={setCreando}
        onSaved={(obra) => navigate(`/businesses/${businessId}/obras/${obra.id}`)}
      />
    </div>
  )
}
